import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompany } from '../context/CompanyContext';
import { useUser } from '../context/UserContext';
import { useAd } from '../context/AdContext';
import { eventsAPI, ticketsAPI } from '../api';
import { useFetch } from '../hooks/useFetch';
import { Card, Button, Badge, LoadingView, ErrorView, EmptyView, Modal, Toast, FieldLabel, FieldInput } from '../components/UI';
import { fmt, categoryEmoji } from '../utils/formatters';
import { statusColor } from '../theme/colors';

function SeatButton({ seat, selected, onClick }) {
    const avail = seat.available !== false;
    const isSel = selected?.seatId === seat.seatId;
    const bg = isSel ? 'var(--brand-500)' : avail ? 'var(--bg-surface)' : '#450a0a';
    const col = isSel ? '#fff' : avail ? 'var(--brand-200)' : 'var(--danger)';
    return (
        <button onClick={() => avail && onClick(seat)} style={{
            padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)',
            backgroundColor: bg, cursor: avail ? 'pointer' : 'not-allowed',
            opacity: avail ? 1 : 0.5, minWidth: 60, textAlign: 'center',
        }}>
            <p style={{ color: col, fontSize: 12, fontWeight: 700 }}>{seat.seatLabel || seat.seat || seat.seatId}</p>
            <p style={{ color: isSel ? 'var(--brand-100)' : 'var(--text-muted)', fontSize: 10 }}>{fmt.money(seat.price, 'USD')}</p>
        </button>
    );
}

function EventCard({ ev, onPress }) {
    const sc = statusColor(ev.status);
    return (
        <Card onClick={onPress} style={{ marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 28 }}>{categoryEmoji(ev.category)}</span>
                <div style={{ flex: 1 }}>
                    <p style={{
                        fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700,
                        color: 'var(--text-primary)', marginBottom: 2
                    }}>{ev.name}</p>
                    <p style={{ color: 'var(--text-muted)', fontSize: 12 }}>{ev.venue} · {ev.city}</p>
                </div>
                <Badge label={ev.status || 'active'} bgColor={sc.bg} color={sc.text} borderColor={sc.border} />
            </div>
            <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>📅 {fmt.date(ev.eventDate)}</span>
                {ev.eventTime && <span style={{ color: 'var(--text-secondary)', fontSize: 13 }}>⏰ {fmt.time(ev.eventTime)}</span>}
            </div>
            {/* Pricing */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {(ev.seats || []).reduce((acc, s) => {
                    const cat = s.category || s.class || 'standard';
                    if (!acc.find(a => a.cat === cat)) acc.push({ cat, price: s.price });
                    return acc;
                }, []).slice(0, 3).map(({ cat, price }) => (
                    <span key={cat} style={{
                        backgroundColor: 'var(--bg-surface)', borderRadius: 'var(--radius-full)',
                        padding: '3px 10px', color: 'var(--brand-200)', fontSize: 12, fontWeight: 600
                    }}>
                        {cat}: {fmt.money(price, ev.currency?.toUpperCase() || 'USD')}
                    </span>
                ))}
                {(ev.gaCapacity > 0 || ev.gaAvailable != null) && (
                    <span style={{
                        backgroundColor: 'var(--bg-surface)', borderRadius: 'var(--radius-full)',
                        padding: '3px 10px', color: 'var(--brand-200)', fontSize: 12, fontWeight: 600
                    }}>
                        GA: {fmt.money(ev.basePrice || 0, ev.currency?.toUpperCase() || 'USD')}
                    </span>
                )}
            </div>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 8 }}>
                <span style={{ color: 'var(--brand-300)', fontSize: 12, fontWeight: 600 }}>{ev.availableSeats ?? 0} Seats Available</span>
            </div>
        </Card>
    );
}

function TicketModal({ visible, event, onClose, onConfirm }) {
    const { profile } = useUser();
    const [selected, setSelected] = useState(null);
    const [qty, setQty] = useState('1');
    const [name, setName] = useState(profile.name || '');
    const [email, setEmail] = useState(profile.email || '');
    const [loading, setLoading] = useState(false);

    React.useEffect(() => {
        if (visible) { setSelected(null); setQty('1'); setName(profile.name || ''); setEmail(profile.email || ''); setLoading(false); }
    }, [visible]);

    const hasGA = event?.gaCapacity > 0 || event?.gaAvailable != null;
    const availSeats = (event?.seats || []).filter(s => s.available !== false);
    const useGA = !selected && hasGA;
    const total = selected ? selected.price : (event?.basePrice || 0) * (parseInt(qty) || 1);

    const handleBuy = async () => {
        if (!selected && !useGA) { Toast.show({ type: 'error', text1: 'Select a seat' }); return; }
        setLoading(true);
        await onConfirm({ event, seat: selected, isGA: useGA, qty: parseInt(qty) || 1, name, email });
        setLoading(false);
    };

    return (
        <Modal visible={visible} onClose={onClose} title={event?.name || 'Buy Ticket'}>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: -8, marginBottom: 12 }}>
                {fmt.date(event?.eventDate)} · {event?.venue}
            </p>

            {availSeats.length > 0 && (
                <>
                    <FieldLabel>Choose Seat</FieldLabel>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4, marginBottom: 8 }}>
                        {availSeats.map(s => <SeatButton key={s.seatId} seat={s} selected={selected} onClick={setSelected} />)}
                    </div>
                </>
            )}

            {hasGA && !selected && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div>
                        <FieldLabel>General Admission Qty</FieldLabel>
                        <FieldInput value={qty} onChange={e => setQty(e.target.value)} type="number" min="1"
                            style={{ width: 80, marginTop: 4 }} />
                    </div>
                    <span style={{ color: 'var(--brand-300)', fontSize: 14, fontWeight: 600, marginTop: 16 }}>
                        × {fmt.money(event?.basePrice || 0, event?.currency?.toUpperCase() || 'USD')}
                    </span>
                </div>
            )}

            <FieldLabel>Your Name</FieldLabel>
            <FieldInput value={name} onChange={e => setName(e.target.value)} placeholder="John Smith" />
            <FieldLabel>Email (For Ticket)</FieldLabel>
            <FieldInput value={email} onChange={e => setEmail(e.target.value)} placeholder="you@yourdomain.com" type="email" />

            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                borderTop: '1px solid var(--border)', marginTop: 16, paddingTop: 16
            }}>
                <span style={{ color: 'var(--text-muted)', fontSize: 14 }}>Total</span>
                <span style={{ color: 'var(--brand-300)', fontSize: 18, fontWeight: 700, fontFamily: 'var(--font-mono)' }}>
                    {fmt.money(total, event?.currency?.toUpperCase() || 'USD')}
                </span>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <Button label="Cancel" variant="outline" onClick={onClose} style={{ flex: 1 }} />
                <Button label="Buy Ticket" onClick={handleBuy} loading={loading} style={{ flex: 1 }} />
            </div>
        </Modal>
    );
}

export default function EventsScreen() {
    const navigate = useNavigate();
    const { company } = useCompany();
    const { profile, updateProfile } = useUser();
    const { maybeShowAd } = useAd();
    const [ticketTarget, setTicketTarget] = useState(null);

    const { data, loading, error, refetch } = useFetch(
        () => eventsAPI.list(company?.companyId, { limit: 30, includeSeats: 'true' }),
        [company?.companyId]
    );
    const events = data?.events || data?.items || [];

    const handleBuyTicket = async ({ event, seat, isGA, qty, name, email }) => {
        try {
            if (name) updateProfile({ name });
            if (email) updateProfile({ email });
            const body = {
                type: 'event', eventId: event.eventId,
                companyId: event.companyId || company.companyId,
                callerName: name, callerEmail: email, callerPhone: profile.phone || null,
                paymentMethod: 'link', calledFrom: 'web',
                unitPrice: seat ? seat.price : event.basePrice || 0,
            };
            if (seat) { body.seatId = seat.seatId; body.quantity = 1; }
            else { body.quantity = qty; }
            const { data: d } = await ticketsAPI.issue(body);
            setTicketTarget(null);
            navigate('/booking-confirm', {
                state: {
                    type: 'ticket', id: d.ticketId,
                    data: { event: event.name, seat: seat?.seatLabel || 'GA', qty },
                    confirmationNumber: d.confirmationNumber,
                    paymentUrl: d.payment?.checkoutUrl || d.checkoutUrl || null,
                    qrUrl: d.qrUrl || null
                },
            });
        } catch (err) {
            Toast.show({ type: 'error', text1: err.response?.data?.error || 'Purchase Failed' });
        }
    };

    if (loading) return <LoadingView label="Loading Events…" />;
    if (error) return <ErrorView message={error} onRetry={refetch} />;

    return (
        <div style={{ padding: 16 }}>
            <h1 style={{
                fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 800,
                marginBottom: 16, color: 'var(--text-primary)'
            }}>Upcoming Events</h1>
            {events.length === 0
                ? <EmptyView emoji="🎟" title="No Upcoming Events" subtitle="Check back soon!" />
                : events.map(ev => (
                    <EventCard key={ev.eventId} ev={ev}
                        onPress={() => { maybeShowAd('tickets'); setTicketTarget(ev); }} />
                ))
            }
            <TicketModal visible={!!ticketTarget} event={ticketTarget}
                onClose={() => setTicketTarget(null)} onConfirm={handleBuyTicket} />
        </div>
    );
}
